import {
  BriefcaseMedical,
  Building2,
  ChevronLeft,
  ChevronRight,
  FileText,
  FolderSymlink,
  HelpCircle,
  MailSearch,
  Inbox,
  MailWarning,
  PhoneOutgoing,
  Search,
  Settings,
  UserRound,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import ExpectedLogo from '@/assets/logos/expected-sm.svg?react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { Sheet, SheetContent } from '@/components/ui/Sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/Tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { ALL_LOCATIONS_LABEL } from '@/lib/allLocations';
import { useUnlinkedFaxCount } from '@/hooks/useUnlinkedFaxCount';
import { prefetchRoute, scheduleIdlePrefetch } from '@/lib/lazyRoute';
import { cn } from '@/lib/utils';
import { CountBadge } from '@/components/ui/CountBadge';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { ContactSupportModal } from '../modals/ContactSupportModal';
import { ClinicSelector } from './ClinicSelector';
import { SendButton } from './SendButton';
import { MASK_PROPS, UNMASK_PROPS } from '@/lib/analytics';

// Define the structure for navigation items
interface NavItemProps {
  path: string;
  label: string;
  icon: React.ElementType; // Component type for the icon
  badge?: number;
  adminOnly?: boolean; // Flag to restrict item to admin users only
}

const adminOnlyNavItems: NavItemProps[] = [
  {
    path: '/all-referrals',
    label: 'All Referrals',
    icon: MailSearch,
    adminOnly: true,
  },
  {
    path: '/admin/patients',
    label: 'Patient Search',
    icon: Search,
    adminOnly: true,
  },
  {
    path: '/admin/clinics',
    label: 'Clinics',
    icon: Building2,
    adminOnly: true,
  },
  {
    path: '/admin/faxes',
    label: 'Inbox',
    icon: Inbox,
    adminOnly: true,
  },
  {
    path: '/calls',
    label: 'Voice calls',
    icon: PhoneOutgoing,
    adminOnly: true,
  },
];

// Define your navigation items data
const navItems: NavItemProps[] = [
  { path: '/referrals', label: 'Referrals', icon: BriefcaseMedical },
  { path: '/patients', label: 'Patient Search', icon: UserRound },
  { path: '/records-requests', label: 'Records Requests', icon: FolderSymlink },
  { path: '/faxes-sent', label: 'All Sent', icon: FileText },
  { path: '/staff', label: 'Staff', icon: Users },
  { path: '/settings', label: 'Settings', icon: Settings },
];

function NavItems({
  title,
  items,
  isCollapsed,
  isCurrentUserAdmin,
  className,
  onNavigate,
  leading,
}: {
  title?: string | null;
  items: NavItemProps[];
  isCollapsed: boolean;
  isCurrentUserAdmin: boolean;
  className?: string;
  onNavigate?: () => void;
  /** Optional element rendered as the first row of the nav group. */
  leading?: React.ReactNode;
}) {
  const location = useLocation();

  // One stable handler for every link: the anchor's own pathname is the route.
  const prefetchOnIntent = useCallback(
    (event: React.SyntheticEvent<HTMLAnchorElement>) =>
      prefetchRoute(event.currentTarget.pathname),
    []
  );

  return (
    <>
      {!isCollapsed && title ? (
        <div className="flex flex-col px-7 mb-4">
          <span className="text-body-small-strong">{title}</span>
        </div>
      ) : null}

      <nav
        // Nav labels are static copy — readable in replay so a session shows
        // where someone navigated. See UNMASK_ATTRIBUTE.
        {...UNMASK_PROPS}
        className={cn(
          'overflow-y-auto overflow-x-hidden px-4 py-4 space-y-1',
          className
        )}
      >
        {leading}
        {items
          .filter(item => !item.adminOnly || isCurrentUserAdmin)
          .map(item => {
            const isActive = location.pathname.startsWith(item.path);
            const LinkIcon = item.icon;

            // The badge is aria-hidden (CountBadge), so the count joins the
            // link's accessible name as sr-only text — a MASK_PROPS text node
            // (not an aria-label) so replay can't record the API-fed count
            // through the unmasked nav.
            const srOnlyBadge = item.badge ? (
              <span className="sr-only" {...MASK_PROPS}>
                {` (${item.badge})`}
              </span>
            ) : null;

            return isCollapsed ? (
              // Collapsed View: Icon + Tooltip
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>
                  <Button
                    asChild // Important: Button renders Link, Link handles navigation
                    variant={isActive ? 'secondary' : 'ghost'} // Use secondary for active bg
                    size="icon"
                    className={cn('w-full justify-center relative')}
                  >
                    <Link
                      to={item.path}
                      onClick={onNavigate}
                      onMouseEnter={prefetchOnIntent}
                      onFocus={prefetchOnIntent}
                    >
                      <LinkIcon />
                      <CountBadge
                        count={item.badge ?? 0}
                        className="absolute -top-1 -right-1 h-4 min-w-4 px-1 bg-primary text-primary-foreground"
                      />
                      <span className="sr-only">
                        {item.label}
                        {srOnlyBadge}
                      </span>{' '}
                      {/* Accessibility */}
                    </Link>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">{item.label}</TooltipContent>
              </Tooltip>
            ) : (
              // Expanded View: Icon + Label + Badge
              <Button
                key={item.path}
                asChild
                variant={isActive ? 'secondary' : 'ghost'}
                className={cn('w-full justify-start gap-3 text-caption-label')}
                size="md"
              >
                <Link
                  to={item.path}
                  onClick={onNavigate}
                  onMouseEnter={prefetchOnIntent}
                  onFocus={prefetchOnIntent}
                >
                  <LinkIcon />
                  <span>{item.label}</span>
                  {srOnlyBadge}
                  <CountBadge
                    count={item.badge ?? 0}
                    className="ml-auto bg-primary text-primary-foreground"
                  />
                </Link>
              </Button>
            );
          })}
      </nav>
    </>
  );
}

interface SidebarProps {
  isMobile?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
}

export function Sidebar({ isMobile, isOpen, onClose }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const location = useLocation();

  const {
    activeClinicId,
    activeClinicName,
    allLocationsActive,
    user,
    logout,
    numPendingClinicInvitations,
  } = useAuth();
  const isCurrentUserAdmin = user?.role === 'ADMIN';

  // On mobile, only poll while the drawer is open — the badge isn't visible
  // otherwise.
  const { data: unlinkedFaxCount = 0 } = useUnlinkedFaxCount({
    enabled: isCurrentUserAdmin && (!isMobile || !!isOpen),
  });
  const adminNavItems = adminOnlyNavItems.map(item =>
    item.path === '/admin/faxes' ? { ...item, badge: unlinkedFaxCount } : item
  );

  // Warm the pages this sidebar links to while idle, so a first click never
  // waits on a chunk; hover/focus on the links covers pages not yet warmed.
  // Desktop only: on a phone the drawer is closed most of the time and the
  // connection is the one most likely to fail mid-warm-up.
  useEffect(() => {
    if (isMobile) return;
    const items = isCurrentUserAdmin
      ? [...adminOnlyNavItems, ...navItems]
      : navItems;
    return scheduleIdlePrefetch([
      ...items.map(item => item.path),
      '/faxes/send',
    ]);
  }, [isCurrentUserAdmin, isMobile]);

  const userInitials =
    user?.firstName || user?.lastName
      ? `${user.firstName.charAt(0).toUpperCase()}${user.lastName.charAt(0).toUpperCase()}`
      : user?.email
        ? user.email.charAt(0).toUpperCase()
        : '';

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const openSupportModal = useCallback(() => setIsSupportModalOpen(true), []);
  const closeSupportModal = useCallback(() => setIsSupportModalOpen(false), []);

  const handleSignOut = async () => {
    setIsUserMenuOpen(false);
    try {
      await logout();
    } catch (error) {
      // Logout is best-effort — AuthContext already clears local session
      // state and redirects even when the API call fails.
    }
  };

  // On mobile, always show expanded; on desktop, use the collapse state
  const effectiveIsCollapsed = isMobile ? false : isCollapsed;

  const sidebarContent = (
    <>
      {/* Header Section */}
      <div
        className={cn(
          'flex items-center px-4 h-8 mb-8 mt-4',
          effectiveIsCollapsed ? 'justify-center' : 'justify-between'
        )}
      >
        {!effectiveIsCollapsed && (
          <div className="flex items-center gap-2 overflow-hidden">
            <div className="flex-shrink-0">
              <ExpectedLogo />
            </div>
          </div>
        )}
        {/* Collapse Button - hidden on mobile */}
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'flex-shrink-0',
              effectiveIsCollapsed ? 'mx-auto' : ''
            )}
            onClick={toggleSidebar}
            aria-label={
              effectiveIsCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
            }
          >
            {effectiveIsCollapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <ChevronLeft className="h-5 w-5" />
            )}
          </Button>
        )}
      </div>

      {/* Admin Navigation Section — Notifications sits above the nav links */}
      {isCurrentUserAdmin && (
        <NavItems
          title="Admin Only"
          items={adminNavItems}
          isCollapsed={effectiveIsCollapsed}
          isCurrentUserAdmin={isCurrentUserAdmin}
          className="mb-8 border-b pb-8"
          onNavigate={isMobile ? onClose : undefined}
          leading={<NotificationBell isCollapsed={effectiveIsCollapsed} />}
        />
      )}

      {/* Clinic Name */}
      {!effectiveIsCollapsed && (allLocationsActive || activeClinicId) ? (
        <div className="flex flex-col px-7 mb-4">
          <span className="text-body-small-strong">
            {allLocationsActive ? ALL_LOCATIONS_LABEL : activeClinicName}
          </span>
        </div>
      ) : null}

      {/* Send button — navigates to the send fax flow */}
      <SendButton
        isCollapsed={effectiveIsCollapsed}
        onNavigate={isMobile ? onClose : undefined}
      />

      {/* Navigation Section */}
      <NavItems
        title={null}
        items={navItems}
        isCollapsed={effectiveIsCollapsed}
        isCurrentUserAdmin={isCurrentUserAdmin}
        className="flex-1"
        onNavigate={isMobile ? onClose : undefined}
      />

      {/* Clinic Selector */}
      <div className="mt-auto pt-4 px-4">
        <div className={cn(effectiveIsCollapsed && 'px-1')}>
          <ClinicSelector isCollapsed={effectiveIsCollapsed} />
        </div>
      </div>

      {/* Contact Support */}
      <div className="px-4 pb-2">
        {effectiveIsCollapsed ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="w-full justify-center"
                onClick={openSupportModal}
              >
                <HelpCircle />
                <span className="sr-only">Contact Support</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="right">Contact Support</TooltipContent>
          </Tooltip>
        ) : (
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-caption-label"
            size="md"
            onClick={openSupportModal}
          >
            <HelpCircle />
            <span>Contact Support</span>
          </Button>
        )}
      </div>

      <ContactSupportModal
        isOpen={isSupportModalOpen}
        onClose={closeSupportModal}
      />

      {/* Footer Section */}
      <div className="p-4">
        <Popover open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
          <PopoverTrigger asChild>
            <div className="flex items-center gap-2 cursor-pointer relative">
              <Avatar className="h-8 w-8">
                <AvatarImage src="foo" />
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              {numPendingClinicInvitations > 0 && (
                <div className="absolute -top-1 left-6 flex h-4 w-4 items-center justify-center bg-red-500 rounded-full">
                  <span className="text-xs font-bold text-white">!</span>
                </div>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-0" align="end" side="top">
            <div className="p-1">
              <Link
                to="/my-profile"
                className={cn(
                  'rounded-sm flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors',
                  location.pathname === '/my-profile'
                    ? 'bg-secondary'
                    : 'hover:bg-secondary'
                )}
                onClick={() => {
                  setIsUserMenuOpen(false);
                  onClose?.();
                }}
              >
                My Profile
              </Link>
              <button
                onClick={handleSignOut}
                className="rounded-sm flex w-full items-center gap-2 px-3 py-2 text-sm hover:bg-secondary transition-colors"
              >
                Sign Out
              </button>
              {numPendingClinicInvitations > 0 && (
                <Link
                  to="/accept-invites"
                  className={cn(
                    'rounded-sm flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors flex flex-row justify-between',
                    location.pathname === '/accept-invites'
                      ? 'bg-secondary'
                      : 'hover:bg-secondary'
                  )}
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    onClose?.();
                  }}
                >
                  <span>View Pending Invites</span>
                  <MailWarning className="h-4 w-4 text-primary" />
                </Link>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={open => !open && onClose?.()}>
        <SheetContent>
          <div className="flex flex-col h-full">{sidebarContent}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <TooltipProvider delayDuration={0}>
      <aside
        className={cn(
          'sticky top-0 h-screen flex flex-col bg-background border-r transition-[width] duration-300 ease-in-out',
          effectiveIsCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {sidebarContent}
      </aside>
    </TooltipProvider>
  );
}
